import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConsentDraftProvider } from './contexts/ConsentDraftContext';
import { ThemeProvider } from './contexts/ThemeContext';
import SchoolMealForm from './components/SchoolMealForm/SchoolMealForm';
import ParentalConsentsPage from './components/ParentalConsentsPage/ParentalConsentsPage';
import MainNav from './components/MainNav/MainNav';
import AuthError from './components/AuthError/AuthError';
import './App.css';
import { ERROR_CODE_FORBIDDEN } from './utils/constants';

const authConfig = {
  clientId: process.env.REACT_APP_OAUTH_CLIENT_ID || '',
  authorizationEndpoint: process.env.REACT_APP_OAUTH_AUTHORIZATION_ENDPOINT || '',
  redirectUri:
    process.env.REACT_APP_OAUTH_REDIRECT_URI ||
    (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : ''),
  scope: process.env.REACT_APP_OAUTH_SCOPE || 'openid profile email',
};

/** Preserve query string (e.g. studentId) when redirecting unknown paths to home */
const FallbackNavigate: React.FC = () => {
  const [sp] = useSearchParams();
  const q = sp.toString();
  return <Navigate to={q ? `/?${q}` : '/'} replace />;
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, error, login } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-text">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || error) {
    const isMissingStudentIdError = error === 'Student ID is missing from URL.';
    const isForbiddenError = error === ERROR_CODE_FORBIDDEN;
    const errorMessage = isForbiddenError
      ? t('auth.error.unauthorized')
      : (error || undefined);

    return (
      <div className="App">
        <AuthError
          message={errorMessage}
          onRetry={isMissingStudentIdError || isForbiddenError ? undefined : login}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <ConsentDraftProvider>
        <MainNav />
        <Routes>
          <Route path="/" element={<SchoolMealForm />} />
          <Route path="/consents" element={<ParentalConsentsPage />} />
          <Route path="*" element={<FallbackNavigate />} />
        </Routes>
      </ConsentDraftProvider>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider config={authConfig}>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
